<template>
  <div class="server-details">
    <!-- Header dos Detalhes -->
    <div class="details-header mb-4">
      <div class="header-top">
        <div class="server-title-section">
          <h1 class="server-title">
            <i class="fas fa-server me-3"></i>
            {{ server.hostname || 'Carregando...' }}
          </h1>
          <div class="server-status-section">
            <span 
              class="server-status-badge" 
              :class="server.is_online ? 'status-online' : 'status-offline'"
            >
              <i :class="server.is_online ? 'fas fa-circle' : 'fas fa-times-circle'" class="me-1"></i>
              {{ server.is_online ? 'Online' : 'Offline' }}
            </span>
            <span class="last-check">
              <i class="fas fa-clock me-1"></i>
              Última verificação: {{ formatDate(server.last_check) }}
            </span>
          </div>
        </div>
        <button class="btn-refresh-details" @click="loadServerDetails" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          <span>Atualizar Dados</span>
        </button>
      </div>
    </div>

    <div class="row g-4">
      <!-- Informações do Servidor -->
      <div class="col-lg-4">
        <div class="info-card">
          <div class="card-header-modern">
            <h3 class="card-title-modern">
              <i class="fas fa-info-circle me-2"></i>
              Informações do Servidor
            </h3>
          </div>
          <div class="card-body-modern">
            <div class="server-info-modern">
              <div class="info-item-modern">
                <div class="info-label">
                  <i class="fas fa-tag me-2"></i>
                  Hostname
                </div>
                <div class="info-value hostname">{{ server.hostname || 'N/A' }}</div>
              </div>
              
              <div class="info-item-modern">
                <div class="info-label">
                  <i class="fas fa-globe me-2"></i>
                  Endereço IP
                </div>
                <div class="info-value ip-address">{{ server.ip_address || 'N/A' }}</div>
              </div>
              
              <div class="info-item-modern">
                <div class="info-label">
                  <i class="fas fa-file-alt me-2"></i>
                  Descrição
                </div>
                <div class="info-value description">{{ server.description || 'Nenhuma descrição disponível' }}</div>
              </div>
              
              <div class="info-item-modern">
                <div class="info-label">
                  <i class="fas fa-heartbeat me-2"></i>
                  Status Atual
                </div>
                <div class="info-value">
                  <span 
                    class="status-badge-detailed" 
                    :class="server.is_online ? 'status-online' : 'status-offline'"
                  >
                    <i :class="server.is_online ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'" class="me-1"></i>
                    {{ server.is_online ? 'Servidor Online' : 'Servidor Offline' }}
                  </span>
                </div>
              </div>
              
              <div class="info-item-modern">
                <div class="info-label">
                  <i class="fas fa-apps me-2"></i>
                  Aplicações Detectadas
                </div>
                <div class="info-value">
                  <span class="applications-count">
                    {{ server.applications ? server.applications.length : 0 }}
                    {{ (server.applications ? server.applications.length : 0) === 1 ? 'aplicação' : 'aplicações' }}
                  </span>
                </div>
              </div>

              <div class="info-item-modern">
                <div class="info-label">
                  <i class="fas fa-history me-2"></i>
                  Última Atualização
                </div>
                <div class="info-value timestamp">{{ formatDate(server.updated_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Métricas Atuais -->
      <div class="col-lg-8">
        <div class="metrics-card">
          <div class="card-header-modern">
            <h3 class="card-title-modern">
              <i class="fas fa-chart-line me-2"></i>
              Métricas em Tempo Real
            </h3>
          </div>
          <div class="card-body-modern">
            <div v-if="server.metrics" class="row g-4">
              <!-- CPU -->
              <div class="col-lg-4 col-md-6">
                <div class="metric-card-modern cpu-card">
                  <div class="metric-header">
                    <div class="metric-icon cpu-icon">
                      <i class="fas fa-microchip"></i>
                    </div>
                    <div class="metric-info">
                      <div class="metric-label">Processador</div>
                      <div class="metric-value">{{ server.metrics.cpu_usage_percent }}%</div>
                    </div>
                  </div>
                  <div class="metric-progress-modern">
                    <div class="progress-background">
                      <div 
                        class="progress-fill-modern" 
                        :class="getProgressClass(server.metrics.cpu_usage_percent, 85)"
                        :style="{ width: server.metrics.cpu_usage_percent + '%' }"
                      ></div>
                    </div>
                    <div class="progress-labels">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div class="metric-footer">
                    <span :class="getCpuStatusClass()">
                      <i class="fas fa-circle me-1"></i>
                      {{ getCpuStatus() }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- RAM -->
              <div class="col-lg-4 col-md-6">
                <div class="metric-card-modern ram-card">
                  <div class="metric-header">
                    <div class="metric-icon ram-icon">
                      <i class="fas fa-memory"></i>
                    </div>
                    <div class="metric-info">
                      <div class="metric-label">Memória RAM</div>
                      <div class="metric-value">{{ server.metrics.ram_used_percent }}%</div>
                    </div>
                  </div>
                  <div class="metric-progress-modern">
                    <div class="progress-background">
                      <div 
                        class="progress-fill-modern" 
                        :class="getProgressClass(server.metrics.ram_used_percent, 90)"
                        :style="{ width: server.metrics.ram_used_percent + '%' }"
                      ></div>
                    </div>
                    <div class="progress-labels">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div class="metric-details">
                    {{ server.metrics.ram_used_gb }}GB / {{ server.metrics.ram_total_gb }}GB
                  </div>
                  <div class="metric-footer">
                    <span :class="getRamStatusClass()">
                      <i class="fas fa-circle me-1"></i>
                      {{ getRamStatus() }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- Disco -->
              <div class="col-lg-4 col-md-6">
                <div class="metric-card-modern disk-card">
                  <div class="metric-header">
                    <div class="metric-icon disk-icon">
                      <i class="fas fa-hdd"></i>
                    </div>
                    <div class="metric-info">
                      <div class="metric-label">Armazenamento</div>
                      <div class="metric-value">{{ server.metrics.disk_used_percent }}%</div>
                    </div>
                  </div>
                  <div class="metric-progress-modern">
                    <div class="progress-background">
                      <div 
                        class="progress-fill-modern" 
                        :class="getProgressClass(server.metrics.disk_used_percent, 80)"
                        :style="{ width: server.metrics.disk_used_percent + '%' }"
                      ></div>
                    </div>
                    <div class="progress-labels">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div class="metric-details">
                    {{ server.metrics.disk_used_gb }}GB / {{ server.metrics.disk_total_gb }}GB
                  </div>
                  <div class="metric-footer">
                    <span :class="getDiskStatusClass()">
                      <i class="fas fa-circle me-1"></i>
                      {{ getDiskStatus() }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="no-metrics-state">
              <div class="no-metrics-icon">
                <i class="fas fa-chart-line"></i>
              </div>
              <h4>Métricas Indisponíveis</h4>
              <p>Nenhuma métrica foi coletada para este servidor ainda.</p>
              <button class="btn-collect" @click="loadServerDetails">
                <i class="fas fa-sync-alt me-1"></i>
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Gráficos Históricos -->
    <div class="row g-4 mt-4">
      <div class="col-lg-12">
        <div class="chart-card">
          <div class="card-header-modern">
            <h3 class="card-title-modern">
              <i class="fas fa-chart-area me-2"></i>
              Histórico de Performance (24 horas)
            </h3>
            <div class="chart-legend">
              <span class="legend-item cpu">
                <i class="fas fa-circle me-1"></i>
                CPU
              </span>
              <span class="legend-item ram">
                <i class="fas fa-circle me-1"></i>
                RAM
              </span>
              <span class="legend-item disk">
                <i class="fas fa-circle me-1"></i>
                Disco
              </span>
            </div>
          </div>
          <div class="card-body-modern">
            <metrics-chart :historical-metrics="server.historical_metrics || []" />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Aplicações -->
    <div class="row g-4 mt-4">
      <div class="col-lg-12">
        <div class="applications-card">
          <div class="card-header-modern">
            <h3 class="card-title-modern">
              <i class="fas fa-layer-group me-2"></i>
              Aplicações Detectadas
            </h3>
            <div class="applications-summary">
              <span class="app-count">
                {{ server.applications ? server.applications.length : 0 }}
                {{ (server.applications ? server.applications.length : 0) === 1 ? 'aplicação' : 'aplicações' }}
              </span>
            </div>
          </div>
          <div class="card-body-modern">
            <applications-list 
              :applications="server.applications || []" 
              :server-id="serverId"
            ></applications-list>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApplicationsList from './ApplicationsList.vue'
import MetricsChart from './MetricsChart.vue'

export default {
  name: 'ServerDetails',
  components: {
    ApplicationsList,
    MetricsChart
  },
  data() {
    return {
      loading: false,
      server: {}
    }
  },
  computed: {
    serverId() {
      return this.$route.params.id
    }
  },
  mounted() {
    this.loadServerDetails()
  },

  methods: {
    async loadServerDetails() {
      this.loading = true
      try {
        const response = await this.$http.get(
          this.$store.getters.api + `server-monitoring/servers/${this.serverId}`
        )
        this.server = response.data.data
        

      } catch (error) {
        console.error('Erro ao carregar detalhes do servidor:', error)
        this.$toast.error('Erro ao carregar detalhes do servidor')
      } finally {
        this.loading = false
      }
    },
    
    getProgressClass(value, threshold) {
      if (value >= threshold) {
        return 'danger'
      } else if (value >= threshold * 0.8) {
        return 'warning'
      } else {
        return 'success'
      }
    },
    
    getCpuStatusClass() {
      if (!this.server.metrics) return 'text-muted'
      const cpu = this.server.metrics.cpu_usage_percent
      return cpu >= 85 ? 'text-danger' : cpu >= 68 ? 'text-warning' : 'text-success'
    },
    
    getCpuStatus() {
      if (!this.server.metrics) return 'N/A'
      const cpu = this.server.metrics.cpu_usage_percent
      return cpu >= 85 ? 'CPU Utilização Alta' : cpu >= 68 ? 'CPU Utilização Moderada' : 'CPU Utilização Baixa'
    },
    
    getRamStatusClass() {
      if (!this.server.metrics) return 'text-muted'
      const ram = this.server.metrics.ram_used_percent
      return ram >= 90 ? 'text-danger' : ram >= 72 ? 'text-warning' : 'text-success'
    },
    
    getRamStatus() {
      if (!this.server.metrics) return 'N/A'
      const ram = this.server.metrics.ram_used_percent
      return ram >= 90 ? 'Memória RAM Utilizada' : ram >= 72 ? 'Memória RAM Utilizada Moderadamente' : 'Memória RAM Utilizada Pouco'
    },
    
    getDiskStatusClass() {
      if (!this.server.metrics) return 'text-muted'
      const disk = this.server.metrics.disk_used_percent
      return disk >= 80 ? 'text-danger' : disk >= 64 ? 'text-warning' : 'text-success'
    },
    
    getDiskStatus() {
      if (!this.server.metrics) return 'N/A'
      const disk = this.server.metrics.disk_used_percent
      return disk >= 80 ? 'Disco Utilizado' : disk >= 64 ? 'Disco Utilizado Moderadamente' : 'Disco Utilizado Pouco'
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('pt-BR')
    }
  }
}
</script>

<style scoped>
/* Layout Principal */
.server-details {
  padding: 30px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

/* Header dos Detalhes */
.details-header {
  margin-bottom: 30px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 25px 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.server-title-section {
  flex: 1;
}

.server-title {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 10px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.server-status-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 10px;
}

.server-status-badge {
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.server-status-badge.status-online {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border: 1px solid #c3e6cb;
}

.server-status-badge.status-offline {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.last-check {
  color: #7f8c8d;
  font-size: 0.85rem;
}

.btn-refresh-details {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 10px;
  color: white;
  padding: 12px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-refresh-details:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.btn-refresh-details:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Cards Modernos */
.info-card, .metrics-card, .chart-card, .applications-card {
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
}

.info-card:hover, .metrics-card:hover, .chart-card:hover, .applications-card:hover {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-header-modern {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 20px 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title-modern {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.card-body-modern {
  padding: 25px;
}

/* Informações do Servidor */
.server-info-modern {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-item-modern {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  border-left: 4px solid #3498db;
  transition: all 0.2s ease;
}

.info-item-modern:hover {
  background: #e3f2fd;
  transform: translateX(5px);
}

.info-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  font-weight: 500;
  color: #2c3e50;
  line-height: 1.4;
}

.info-value.hostname {
  font-size: 1.1rem;
  font-weight: 600;
  color: #667eea;
}

.info-value.ip-address {
  font-family: 'Monaco', 'Consolas', monospace;
  background: #f1f3f4;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.info-value.description {
  font-style: italic;
  color: #6c757d;
}

.info-value.timestamp {
  font-size: 0.9rem;
  color: #7f8c8d;
}

.status-badge-detailed {
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.status-badge-detailed.status-online {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-badge-detailed.status-offline {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.applications-count {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
  color: #2c3e50;
  padding: 4px 12px;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.9rem;
}

/* Cards de Métricas Modernos */
.metric-card-modern {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  height: 100%;
}

.metric-card-modern:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
}

.cpu-card {
  border-left: 4px solid #667eea;
}

.ram-card {
  border-left: 4px solid #4facfe;
}

.disk-card {
  border-left: 4px solid #fa709a;
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.metric-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.cpu-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.ram-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.disk-icon {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.metric-info {
  flex: 1;
}

.metric-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}

.metric-value {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

/* Barras de Progresso Modernas */
.metric-progress-modern {
  margin: 15px 0;
}

.progress-background {
  background: #f1f3f4;
  border-radius: 10px;
  height: 12px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-fill-modern {
  height: 100%;
  border-radius: 10px;
  transition: width 0.8s ease;
  position: relative;
}

.progress-fill-modern.success {
  background: linear-gradient(90deg, #4caf50 0%, #81c784 100%);
}

.progress-fill-modern.warning {
  background: linear-gradient(90deg, #ff9800 0%, #ffb74d 100%);
}

.progress-fill-modern.danger {
  background: linear-gradient(90deg, #f44336 0%, #e57373 100%);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #9e9e9e;
  margin-top: 5px;
}

.metric-details {
  font-size: 0.8rem;
  color: #7f8c8d;
  text-align: center;
  margin: 10px 0;
  font-weight: 500;
}

.metric-footer {
  text-align: center;
  margin-top: 15px;
}

.metric-footer span {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

/* Estados sem Métricas */
.no-metrics-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.no-metrics-icon {
  font-size: 4rem;
  color: #dee2e6;
  margin-bottom: 20px;
}

.no-metrics-state h4 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
}

.no-metrics-state p {
  font-size: 1rem;
  margin-bottom: 25px;
}

.btn-collect {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  color: white;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-collect:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
}

/* Legenda do Gráfico */
.chart-legend {
  display: flex;
  gap: 20px;
  align-items: center;
}

.legend-item {
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.legend-item.cpu {
  color: #667eea;
}

.legend-item.ram {
  color: #4facfe;
}

.legend-item.disk {
  color: #fa709a;
}

/* Aplicações */
.applications-summary {
  display: flex;
  align-items: center;
}

.app-count {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
  color: #2c3e50;
  padding: 6px 15px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .server-details {
    padding: 15px;
  }
  
  .header-top {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .server-title {
    font-size: 1.5rem;
  }
  
  .server-status-section {
    flex-direction: column;
    gap: 10px;
  }
  
  .metric-card-modern {
    margin-bottom: 20px;
  }
  
  .chart-legend {
    flex-wrap: wrap;
    gap: 10px;
  }
}

/* Animações */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-card, .metrics-card, .chart-card, .applications-card {
  animation: fadeInUp 0.6s ease forwards;
}

.metric-card-modern {
  animation: fadeInUp 0.8s ease forwards;
}

/* Cores dos Status */
.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}
</style> 